/**
 * Live-Liste: laufende Regierungen angemeldeter Spieler.
 * Die Kennzahlen prüft und schreibt der Server (verify-run, Modus „live“) aus dem Cloud-Spielstand.
 */
import { supabase } from '../../lib/supabase';
import type { GameState } from '../../engine/types';
import { liveSummary } from '../../engine/score';

export type LiveRow = {
  username: string | null; party: string; leg: number; since_year: number; year: number; months: number;
  poll: number | null; difficulty: string; live_at: string | null; updated_at: string; verified?: boolean; mine: boolean; auto?: boolean;
};

export async function liveList(limit = 25): Promise<LiveRow[] | null> {
  if (!supabase) return [];
  const { data, error } = await supabase.rpc('live_governments', { lim: limit });
  if (error) return null; // Migration 0008 fehlt
  return (data as LiveRow[]) ?? [];
}

/** Eigener Eintrag (auch wenn er nicht unter den ersten 25 ist). */
export async function myLive(): Promise<{ live: any; live_at: string | null; verified: boolean } | null> {
  if (!supabase) return null;
  const { data: u } = await supabase.auth.getUser(); if (!u.user) return null;
  let r: any = await supabase.from('saves').select('live,live_at,live_claim,seed:state->seed').eq('user_id', u.user.id).eq('slot', 1).maybeSingle();
  if (r.error) r = await supabase.from('saves').select('live,live_at,seed:state->seed').eq('user_id', u.user.id).eq('slot', 1).maybeSingle();
  const d: any = r.data; if (!d) return null;
  const same = (x: any) => x && String(x.seed) === String(d.seed);
  return same(d.live) ? { live: d.live, live_at: d.live_at, verified: true } : same(d.live_claim) ? { live: d.live_claim, live_at: null, verified: false } : { live: null, live_at: null, verified: false };
}

let lastLeg = -1, lastSeed: number | undefined, lastAt = 0;
/**
 * Nach dem Cloud-Speichern: den Server um Prüfung bitten. Höchstens alle 10 Minuten und sofort,
 * wenn eine neue Legislatur oder eine neue Partie beginnt. Nur für Partien, in denen man regiert.
 */
export function maybeVerifyLive(S: GameState) {
  if (!supabase || !S || S.mp) return;
  const governs = S.career.length > 0 || ['govern', 'review'].includes(S.phase);
  if (!governs) return;
  const now = Date.now(), changed = S.leg !== lastLeg || S.seed !== lastSeed;
  if (!changed && now - lastAt < 10 * 60 * 1000) return;
  lastLeg = S.leg; lastSeed = S.seed; lastAt = now;
  void verifyLiveNow();
}

/** Prüfung jetzt anstoßen (z. B. beim Öffnen der Bestenliste). Gibt null bei Erfolg zurück, sonst den Grund. */
export async function verifyLiveNow(): Promise<string | null> {
  if (!supabase) return 'offline';
  try {
    const { data, error } = await supabase.functions.invoke('verify-run', { body: { mode: 'live' } });
    if (data?.ok) return null;
    if (data?.reason) return String(data.reason);
    const ctx: any = (error as any)?.context;
    if (ctx?.json) { try { const j = await ctx.json(); if (j?.reason) return String(j.reason); } catch { /* egal */ } }
    return error ? 'Server-Prüfung nicht erreichbar' : 'unbekannt';
  } catch { return 'Server-Prüfung nicht erreichbar'; }
}

/** Beim Öffnen der Bestenliste: Kennzahlen der laufenden Partie melden, ohne den Spielstand zu überschreiben. */
export async function claimLiveNow(S: GameState | null): Promise<void> {
  if (!supabase || !S || S.mp) return;
  const { data: u } = await supabase.auth.getUser(); if (!u.user) return;
  try { await supabase.from('saves').update({ live_claim: liveSummary(S) }).eq('user_id', u.user.id).eq('slot', 1); } catch { /* Spalte fehlt (Migration 0009) */ }
}

export async function setLiveVisible(on: boolean): Promise<string | null> {
  if (!supabase) return 'Nicht verbunden.';
  const { data: u } = await supabase.auth.getUser(); if (!u.user) return 'Nicht angemeldet.';
  const { error } = await supabase.from('profiles').update({ live_visible: on }).eq('id', u.user.id);
  return error ? (/live_visible/.test(error.message) ? 'Die Live-Liste ist in der Datenbank noch nicht eingerichtet (Migration 0008).' : error.message) : null;
}
